import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import ReactDom from 'react-dom';
import {
  ModelInput,
  type ModelInputProps,
  type ModelInputUploadProgressChange,
} from '@meitu/roboneo-landingpage/model-input';
import '@meitu/roboneo-landingpage/styles.css';
import { message } from '$/components/Message';
import DownloadPreferencePopup from '$/components/DownloadPreferencePopup';
import {
  LanguageCode,
  replaceTemplateWithElements,
  useLanguageStore,
} from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import InputTextAreaV2 from '$/components/InputTextAreaV2';
import { openVipModal } from '$/store/useVipModalStore';
import { setUploadThread } from '$/store/useUploadThreadStore';
import { downloadFileFromUrl } from '$/utils/download';
import {
  getAigcWaterMarkSwitch,
  isNoBrandWatermarkGuideMedia,
  resolveNoBrandWatermarkDownloadUrl,
  resolveNoBrandWatermarkDownloadUrlByApi,
} from '$/utils/noBrandWatermarkDownload';
import { getEnv, navigate } from '@/common/agents';
import { getCookieHomeUrl } from '@/common/homeVersionCookie';
import { useCommonStore } from '@/store/useCommonStore';

type ModelInputLocale = NonNullable<ModelInputProps['languageCode']>;
type ModelInputTranslationParams = Record<string, string>;
type ModelInputTranslateWithParams = (
  key: string,
  params?: ModelInputTranslationParams,
) => string;

const MODEL_INPUT_LOCALE_MAP: Partial<Record<LanguageCode, ModelInputLocale>> = {
  [LanguageCode.zhHans]: 'zh',
  [LanguageCode.zh]: 'zh',
  [LanguageCode.en]: 'en',
  [LanguageCode.pt]: 'pt',
  [LanguageCode.ja]: 'ja',
  [LanguageCode.de]: 'de',
  [LanguageCode.kor]: 'kor',
  [LanguageCode.es]: 'es',
  [LanguageCode.th]: 'th',
};

/** 通过主站消息组件展示 ModelInput 返回的配置或执行错误。 */
function showModelInputError(errorMsg: string) {
  message.error(errorMsg, 3);
}

/** 余额不足时复用主站订阅弹窗，并标记为功能算力不足入口。 */
function openModelInputVipModal() {
  openVipModal('func_token_short');
}

/** 将 ModelInput SDK 的上传进度同步到主站统一上传 Toast。 */
const updateModelInputUploadProgress: ModelInputUploadProgressChange = (taskId, state) => {
  setUploadThread(taskId, state);
};

/** 按主站新旧首页偏好及当前环境返回首页。 */
function goToHome() {
  navigate({
    mode: 'url',
    url: getCookieHomeUrl(getEnv().ENV === 'development'),
  });
}

interface ModelInputPageProps {
  id: string;
}

/** 为落地页注入主站账号、环境及国际化上下文。 */
function ModelInputPage({
  id,
}: ModelInputPageProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  /** 固定 Portal 宿主节点身份，仅在全屏状态变化时移动 DOM，避免 ModelInput 被反复卸载重建。 */
  const portalHost = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const host = document.createElement('div');
    host.dataset.modelInputPortalHost = '';
    host.style.display = 'contents';
    return host;
  }, []);
  let portalTarget: Element | null = null;
  if (typeof document !== 'undefined') {
    portalTarget = isFullScreen ? document.body : document.querySelector(`#${id}`);
  }
  const env = useOfficialStore((state) => state.env);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const currentCountryCode = useOfficialStore((state) => state.currentCountryCode);
  const userInfo = useOfficialStore((state) => state.userInfo);
  const isNoBrandWatermarkMember = useOfficialStore(
    (state) => Boolean(state.userInfo.subscribe?.isValid),
  );
  const languageCode = useLanguageStore((state) => state.languageStore.languageCode);
  const smartcatTranslationText = useLanguageStore(
    (state) => state.languageStore.smartcatTranslationText,
  );
  const toLogin = useCommonStore((state) => state.toLogin);
  const modelInputLocale = MODEL_INPUT_LOCALE_MAP[languageCode] ?? 'en';

  /**
   * 将 ModelInput 内置文案 key 交给主站 Smartcat 翻译。
   * 未命中时返回空串，由 landingpage SDK 回退到中文默认文案。
   */
  const translateModelInputText = useCallback<ModelInputTranslateWithParams>(
    (key, params) => (
      params === undefined
        ? smartcatTranslationText((v) => v[key])
        : replaceTemplateWithElements(key, params)
    ),
    [smartcatTranslationText],
  );

  /** 与画布下载保持一致：前端收集水印偏好，图片/视频由后端结合会员身份解析最终地址。 */
  const downloadModelInputMedia = useCallback<NonNullable<ModelInputProps['onDownload']>>(
    async (item) => {
      const originalUrl = item.url.trim();
      const watermarkUrl = item.watermarkUrl?.trim();
      const mediaType = item.mimeType.split('/')[0]?.toLowerCase();
      const shouldResolveMemberWatermark = isNoBrandWatermarkGuideMedia(mediaType);
      const hasDistinctWatermark = Boolean(watermarkUrl && watermarkUrl !== originalUrl);
      let downloadUrl = originalUrl;

      if (shouldResolveMemberWatermark && isNoBrandWatermarkMember) {
        downloadUrl = resolveNoBrandWatermarkDownloadUrl({
          url: originalUrl,
          watermarkUrl,
        });
      } else if (!isOversea && hasDistinctWatermark) {
        const preference = await DownloadPreferencePopup.show();
        if (preference === '2') return;
        if (preference === '1' && watermarkUrl) downloadUrl = watermarkUrl;
      }

      if (shouldResolveMemberWatermark) {
        downloadUrl = await resolveNoBrandWatermarkDownloadUrlByApi({
          url: originalUrl,
          watermarkUrl,
          aigcWaterMarkSwitch: getAigcWaterMarkSwitch(),
          fallbackUrl: downloadUrl,
        });
      }

      await downloadFileFromUrl(downloadUrl, item.name, { preferSuggestedFilename: true });
    },
    [isNoBrandWatermarkMember, isOversea],
  );

  const commonParams = useMemo(() => ({
    uid: userInfo.uid || '',
    client_language: languageCode,
    lang: languageCode,
    country_code: currentCountryCode,
    area_code: currentCountryCode,
  }), [currentCountryCode, languageCode, userInfo.uid]);
  const commonHeaders = useMemo<NonNullable<ModelInputProps['commonHeaders']>>(() => {
    const headers: NonNullable<ModelInputProps['commonHeaders']> = {};
    if (userInfo.token) {
      headers['access-token'] = userInfo.token;
    }
    return headers;
  }, [userInfo.token]);

  const [targetMediaType, setTargetMediaType] = useState<'video' | 'image' | undefined>(undefined);
  const [targetModelId, setTargetModelId] = useState<string | undefined>(undefined);

  useEffect(() => {
    console.log('landingPageConfig', window.landingPageConfig?.agentInput);
    setTargetMediaType(window.landingPageConfig?.agentInput?.targetMediaType ?? undefined);
    setTargetModelId(window.landingPageConfig?.agentInput?.targetModelId ?? undefined);
  }, [commonParams, commonHeaders]);

  useEffect(() => {
    if (!portalHost || !portalTarget) return undefined;
    portalTarget.appendChild(portalHost);
    return () => {
      if (portalHost.parentNode === portalTarget) portalHost.remove();
    };
  }, [portalHost, portalTarget]);

  if (!portalHost || !portalTarget) return null;

  return ReactDom.createPortal(
    <ModelInput
      env={env}
      targetMediaType={targetMediaType}
      targetModelId={targetModelId}
      uid={userInfo.uid}
      isOversea={isOversea}
      InputTextAreaV2={InputTextAreaV2}
      languageCode={modelInputLocale}
      translate={translateModelInputText}
      commonParams={commonParams}
      commonHeaders={commonHeaders}
      onToastErrorMsg={showModelInputError}
      onUploadProgressChange={updateModelInputUploadProgress}
      onLogin={toLogin}
      onOpenVipModal={openModelInputVipModal}
      onDownload={downloadModelInputMedia}
      onGoHome={goToHome}
      onFullscreenChange={setIsFullScreen}
    />,
    portalHost,
  );
}

export default ModelInputPage;
